<script lang="ts">
  import { onMount } from 'svelte';
  import { beginLogin, clientId, customClientIdActive, isValidClientId, setCustomClientId } from '../spotify/auth';

  /**
   * Walkthrough for hosting with your own Spotify developer app (PKCE needs only the Client ID).
   * `onlogin` starts the login after saving (defaults to a plain Spotify login).
   */
  let { onclose, onlogin = () => void beginLogin() }: { onclose: () => void; onlogin?: () => void } = $props();

  const DASHBOARD_URL = 'https://developer.spotify.com/dashboard/create';
  const redirect = `${location.origin}/callback`;

  let value = $state(customClientIdActive() ? clientId() : '');
  let error = $state('');
  let copied = $state(false);
  let input: HTMLInputElement;

  // Focus moves into the dialog on open and back to whatever opened it on close.
  onMount(() => {
    const opener = document.activeElement as HTMLElement | null;
    input.focus();
    return () => opener?.focus?.();
  });

  async function copyRedirect() {
    try {
      await navigator.clipboard.writeText(redirect);
    } catch {
      // Older/insecure contexts: fall back to a hidden textarea.
      const ta = document.createElement('textarea');
      ta.value = redirect;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* user can still select the text */ }
      ta.remove();
    }
    copied = true;
    setTimeout(() => (copied = false), 1800);
  }

  function save(e: SubmitEvent) {
    e.preventDefault();
    if (!isValidClientId(value)) {
      error = "That doesn't look like a Client ID. It's 32 letters and numbers (0–9 and a–f).";
      return;
    }
    if (!setCustomClientId(value)) {
      error = "This browser won't let BANGER! save it (private mode?). Try a normal window.";
      return;
    }
    error = '';
    onlogin();
  }

  function onkey(e: KeyboardEvent) {
    if (e.key === 'Escape') onclose();
  }
</script>

<svelte:window onkeydown={onkey} />

<div class="overlay" role="dialog" aria-modal="true" aria-labelledby="own-app-title">
  <main>
    <header>
      <button type="button" class="back" onclick={onclose}>← Back</button>
      <h1 id="own-app-title" class="wordmark">Use your own Spotify app</h1>
    </header>
    <p class="intro">Takes about 3 minutes. You make a free Spotify developer app, and BANGER! plays through it.</p>

    <ol class="steps">
      <li>
        <b>1</b>
        <div>
          <p>You'll need <strong>Spotify Premium</strong>. Open the Spotify Developer Dashboard and log in.</p>
          <a class="action" href={DASHBOARD_URL} target="_blank" rel="noopener noreferrer">Open the Developer Dashboard ↗</a>
        </div>
      </li>
      <li>
        <b>2</b>
        <div><p><strong>App name:</strong> anything (e.g. BANGER! party). <strong>App description:</strong> anything. <strong>Website:</strong> leave blank.</p></div>
      </li>
      <li>
        <b>3</b>
        <div>
          <p><strong>Redirect URI:</strong> paste this exactly, then click <strong>Add</strong>:</p>
          <div class="copyrow">
            <code>{redirect}</code>
            <button type="button" class="action" onclick={copyRedirect}>{copied ? 'Copied!' : 'Copy'}</button>
          </div>
        </div>
      </li>
      <li>
        <b>4</b>
        <div><p>Under <em>“Which API/SDKs are you planning to use?”</em> check <strong>Web API</strong> and <strong>Web Playback SDK</strong>. Agree to the terms and click <strong>Save</strong>.</p></div>
      </li>
      <li>
        <b>5</b>
        <form onsubmit={save} novalidate>
          <label for="own-client-id">Copy your app's <strong>Client ID</strong> (on the app's Settings page) and paste it here:</label>
          <input
            id="own-client-id" type="text" bind:value bind:this={input} autocomplete="off" spellcheck="false"
            placeholder="32 letters and numbers" aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? 'own-client-id-err' : undefined}
            oninput={() => (error = '')}
          />
          {#if error}<p id="own-client-id-err" class="err" role="alert">{error}</p>{/if}
          <div class="save"><b class="n6">6</b><button type="submit" class="btn-neon">Save &amp; log in with my app</button></div>
        </form>
      </li>
    </ol>

    <p class="foot">Your Client ID is stored only in this browser. It isn't secret — BANGER! never needs your client secret. As the app's owner you're automatically allowed to use it; to let friends host with it, add their Spotify emails under User Management in the dashboard.</p>
  </main>
</div>

<style>
  .overlay { position: fixed; inset: 0; z-index: 100; overflow-y: auto; text-align: left; background: radial-gradient(ellipse at 50% 0%, var(--bg-1), var(--bg-0) 70%), var(--bg-0); }
  main { max-width: 1000px; margin: 0 auto; padding: 24px 16px 48px; font-size: clamp(1.05rem, 1.3vw, 1.3rem); line-height: 1.45; }
  header { display: grid; gap: 8px; justify-items: start; }
  h1 { margin: 0; justify-self: center; text-align: center; font-size: clamp(2rem, 3.2vw, 3.2rem); }
  .intro { margin: 10px 0 4px; text-align: center; opacity: 0.85; }
  .back, .action {
    font: inherit; font-weight: 800; color: var(--cyan); text-decoration: none; cursor: pointer;
    padding: 0.4em 1.1em; border-radius: 999px; background: rgba(125, 249, 255, 0.08); border: 2px solid rgba(125, 249, 255, 0.5);
    display: inline-flex; align-items: center; min-height: 48px;
  }
  .back { white-space: nowrap; }
  .action:hover, .back:hover { box-shadow: 0 0 14px rgba(125, 249, 255, 0.5); }
  .steps { list-style: none; margin: 0; padding: 0; display: grid; gap: 12px; }
  .steps > li {
    display: flex; gap: 14px; align-items: flex-start;
    background: rgba(10, 6, 24, 0.55); border: 1px solid rgba(255, 255, 255, 0.14); border-radius: 18px; padding: 14px 16px; margin-top: 4px;
  }
  .steps > li > div, form { flex: 1; min-width: 0; display: grid; gap: 10px; justify-items: start; }
  .steps b, .n6 {
    flex: none; width: 2em; height: 2em; border-radius: 50%; display: grid; place-items: center;
    background: var(--pink); color: #0a0618; font-weight: 900; box-shadow: 0 0 12px rgba(255, 61, 242, 0.6);
  }
  p { margin: 0; }
  .copyrow { display: flex; flex-wrap: wrap; gap: 10px; align-items: center; max-width: 100%; }
  code {
    font-size: 1.05em; padding: 0.35em 0.7em; border-radius: 10px; background: rgba(0, 0, 0, 0.45);
    border: 1px solid rgba(125, 249, 255, 0.35); color: #fff; overflow-wrap: anywhere; user-select: all;
  }
  label { display: block; }
  input {
    width: 100%; max-width: 28em; font: inherit; font-family: ui-monospace, Menlo, monospace; color: #fff;
    padding: 0.5em 0.8em; min-height: 48px; border-radius: 12px; background: rgba(0, 0, 0, 0.45);
    border: 2px solid rgba(255, 255, 255, 0.3);
  }
  input:focus { outline: none; border-color: var(--cyan); box-shadow: 0 0 12px rgba(125, 249, 255, 0.45); }
  input[aria-invalid='true'] { border-color: #ff8fa3; }
  .err { color: #ff8fa3; font-weight: 700; }
  .save { display: flex; gap: 14px; align-items: center; margin-top: 4px; }
  .save .btn-neon { font-size: 1.15em; padding: 0.5em 1.5em; }
  .foot { margin-top: 18px; font-size: 0.9em; opacity: 0.75; }

  @media (min-width: 1100px) {
    header { grid-template-columns: 1fr auto 1fr; align-items: center; }
    header h1 { grid-column: 2; }
  }
</style>
